import React, { } from "react";
import { useSound } from "../../utils/sound/SoundProvider";
import useGameBase from '../../hooks/useGameBase';

const CARD_BACK_IMG = "/cards/card_back.png";
const CARD_FACES = [
  "🍎","🍌","🍇","🍉","🍒","🍋","🍓","🍑","🥝","🥥","🥕","🌽","🍆","🥔","🍄","🥦","🥬","🥒","🌶","🧄","🧅","🥜","🌰","🍞","🧀","🥚","🍗","🍖","🥩","🥓","🍔"
];

function getFaceIcon(face) {
  // Use emoji, fallback to number if not enough
  return CARD_FACES[face % CARD_FACES.length] || face;
}

function MatchPairsGame({ gameState, gamePlayers, user, onGameAction, onPlayAgain, socket, roomId }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const [flashMatched, setFlashMatched] = React.useState([]);

  // Memoize cards to avoid lint warning and ensure stable reference
  const cards = React.useMemo(() => gameState.cards || [], [gameState.cards]);

  // sound
  const sound = useSound();
  const prevMatchedRef = React.useRef(new Set());

  // Play a positive sound when new matches are detected in the game state
  React.useEffect(() => {
    try {
      const matchedIds = new Set((cards || []).filter(c => c.matched).map(c => c.id));
      const prev = prevMatchedRef.current || new Set();
      // find any id that is in matchedIds but not in prev
      let newMatchFound = false;
      matchedIds.forEach(id => { if (!prev.has(id)) newMatchFound = true; });
      if (newMatchFound) {
        // play positive sound; ASSETS defines 'ui:positive'
        try { sound && sound.play && sound.play('ui:positive'); } catch (e) {}
      }
      prevMatchedRef.current = matchedIds;
    } catch (e) {
      // ignore
    }
  }, [cards, sound]);


  // Move localSelected above useEffect to fix lint error
  const [localSelected, setLocalSelected] = React.useState([]);

  // Flash animation: show yellow ring when two cards are revealed and they match
  React.useEffect(() => {
    if (localSelected.length === 2) {
      const [firstIdx, secondIdx] = localSelected;
      const firstCard = cards[firstIdx];
      const secondCard = cards[secondIdx];
      if (firstCard && secondCard && firstCard.face === secondCard.face) {
        setFlashMatched([firstIdx, secondIdx]);
        setTimeout(() => setFlashMatched([]), 600); // flash for 600ms
      }
    } else {
      setFlashMatched([]);
    }
  }, [localSelected, cards]);
  const localPlayer = user.socketId;
  const myTurn = localPlayer === gameState.currentPlayer;
  const winners = gameState.winners || [];

  function onCardClick(idx) {
    //console.log("onCardClick", { idx, myTurn, currentPlayer: gameState.currentPlayer, localPlayer, card: cards[idx], localSelected });
    if (!myTurn) {
      //console.log("Not your turn");
      return;
    }
    const card = cards[idx];
    if (card.revealed || card.matched) {
      //console.log("Card already revealed or matched", card);
      return;
    }
  // Send pick action
  submitAction({ type: "pick", cardIdx: idx }).catch(()=>{});
    // Track locally selected cards
    const newSelected = [...localSelected, idx];
    setLocalSelected(newSelected);
    if (newSelected.length === 2) {
      // After 2nd pick, send endTurn to server and reset local selection
      const delayMs = (gameState.cardsShownFor ? Number(gameState.cardsShownFor) : 1) * 1000;
      setTimeout(() => {
        submitAction({ type: "endTurn" }).catch(()=>{});
        setLocalSelected([]);
      }, delayMs);
    }
  }

  // Reset localSelected if turn changes or gameState.selected resets
  React.useEffect(() => {
    if (gameState.selected?.length === 0) {
      setLocalSelected([]);
    }
    if (!myTurn) {
      setLocalSelected([]);
    }
  }, [gameState.selected, myTurn]);

    // Calculate grid columns and rows based on gameState.size
  const gridSize = gameState.size || cards.length || 24;
  // Prefer 4 columns for 12, 24, 36, 48, 60; fallback to square-ish for other sizes
  let columns = 4;
  if (gridSize % 6 === 0 && gridSize / 6 >= 2) {
    columns = 6;
  } else if (gridSize % 5 === 0 && gridSize / 5 >= 2) {
    columns = 5;
  } else if (gridSize % 4 === 0) {
    columns = 4;
  } else {
    columns = Math.ceil(Math.sqrt(gridSize));
  }

  return (
    <div className="flex flex-col items-center w-full">
      <style>{`
        @keyframes wobbleCard { 0% { transform: rotate(2deg) scale(1.00); } 25% { transform: rotate(0deg) scale(1.00); } 50% { transform: rotate(-2deg) scale(1.00); } 75% { transform: rotate(0deg) scale(1.00); }  100% { transform: rotate(2deg) scale(1.0); } }
        .card-wobble { animation: wobbleCard 1.2s infinite; }
      `}</style>
      <div className="grid gap-2 mb-4" style={{ gridTemplateColumns: `repeat(${columns}, 48px)` }}>
        {cards.map((card, idx) => {
          const isFlipped = card.revealed || card.matched;
          const canClick = myTurn && !isFlipped;
          const isFlashing = flashMatched.includes(idx);
          // Only animate eligible cards if less than two are selected
          const wobbleClass = (canClick && localSelected.length < 2) ? "card-wobble" : "";
          return (
            <div
              key={card.id}
              className={`relative w-12 h-16 select-none transition-transform duration-300 ${isFlipped ? "rotate-y-180" : ""} ${canClick ? "cursor-pointer" : "cursor-not-allowed"} ${isFlashing ? "ring-4 ring-yellow-400 dark:ring-yellow-300 animate-pulse" : ""} ${wobbleClass}`}
              style={{ perspective: 600 }}
              onClick={canClick ? () => onCardClick(idx) : undefined}
            >
              <div
                className={`absolute inset-0 flex items-center justify-center shadow-md bg-white dark:bg-gray-700 w-12 h-16 transition-transform duration-300 ${isFlipped ? "opacity-100" : "opacity-0"}`}
                style={{ backfaceVisibility: "hidden", transform: isFlipped ? "rotateY(0deg)" : "rotateY(180deg)" }}
              >
                {isFlipped ? (
                  <span className="flex items-center justify-center w-full h-full text-2xl">
                    {getFaceIcon(card.face)}
                  </span>
                ) : null}
              </div>
              <div
                className={`absolute inset-0 flex items-center justify-center rounded-lg shadow bg-gray-400 dark:bg-gray-800 transition-transform duration-300 ${isFlipped ? "opacity-0" : "opacity-100"}`}
                style={{ backfaceVisibility: "hidden", transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)" }}
              >
                <img src={CARD_BACK_IMG} alt="Card back" className="w-12 h-16 object-contain shadow-md" />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mb-2">
        <span className="font-semibold text-gray-800 dark:text-gray-200">Scores:</span>
        <ul className="inline-block ml-2">
          {gameState.players?.map((p) => (
            <li
              key={p.socketIdid}
              className={
                "inline-block mr-3 " +
                (winners.includes(p.socketId)
                  ? "font-bold text-blue-700 dark:text-blue-300"
                  : "text-gray-800 dark:text-gray-200")
              }
            >
              {p.username}: {p.score}
            </li>
          ))}
        </ul>
      </div>
      {gameState.finished && (
        <div className="text-lg font-bold text-blue-700 my-2">
          Game Over! Winner{winners.length > 1 ? "s" : ""}: {gameState.players.filter(p => winners.includes(p.socketId)).map(p => p.username).join(", ")}
        </div>
      )}
    </div>
  );
}

MatchPairsGame.Guide = function MatchPairsGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Match Pairs</h3>
      <ul className="list-disc ml-6">
        <li>Flip two cards at a time to find matching pairs.</li>
        <li>Remember card positions to improve your score.</li>
        <li>Most pairs at the end wins.</li>
      </ul>
    </div>
  );
};

export default MatchPairsGame;
